import React, { ReactNode, useCallback } from 'react';
import {
    Box,
    ClickAwayListener,
    Dialog,
    DialogActions,
    DialogActionsProps,
    DialogContent,
    DialogContentProps,
    DialogProps,
    DialogTitle,
    IconButton,
    SxProps,
    Theme,
} from '@mui/material';
import { HeaderDialogType } from '@gui/constants/Panels';
import { XClose } from '@untitledui-pro/icons/line';

type DialogSide = 'left' | 'right';

export const HEADER_HEIGHT = 44;
export const COLUMN_WIDTH = 270;

export interface SidePanelDialogProps {
    open?: HeaderDialogType | boolean | null;
    onClose: () => void;
    children: ReactNode;
    side?: DialogSide;
    horizontalMargin?: number;
    disableBackdropClose?: boolean;
    offset?: number;
    containerSx?: SxProps<Theme>;
    paperSx?: SxProps<Theme>;
    dialogProps?: Omit<DialogProps, 'open' | 'onClose'>;
    title?: ReactNode;
    headerActions?: ReactNode;
    showCloseButton?: boolean;
    footer?: ReactNode;
    contentProps?: DialogContentProps;
    actionsProps?: DialogActionsProps;
}

const combineSx = (...styles: Array<SxProps<Theme> | undefined>): SxProps<Theme> | undefined => {
    const filtered = styles.filter(Boolean) as SxProps<Theme>[];
    if (filtered.length === 0) return undefined;
    if (filtered.length === 1) return filtered[0];
    return filtered as SxProps<Theme>;
};

const SidePanelDialog: React.FC<SidePanelDialogProps> = ({
    open,
    onClose,
    children,
    side = 'left',
    horizontalMargin = 8,
    disableBackdropClose = true,
    offset = 8,
    containerSx,
    paperSx,
    dialogProps,
    title,
    headerActions,
    showCloseButton = true,
    footer,
    contentProps,
    actionsProps,
}) => {
    const {
        sx: customSx,
        PaperProps: customPaperProps,
        hideBackdrop,
        disableAutoFocus,
        disableEnforceFocus,
        fullWidth,
        maxWidth,
        keepMounted,
        ...restDialogProps
    } = dialogProps ?? {};

    const handleClose = useCallback(
        (_event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
            if (disableBackdropClose && reason === 'backdropClick') return;
            onClose();
        },
        [disableBackdropClose, onClose]
    );

    const baseDialogSx: SxProps<Theme> = {
        pointerEvents: 'none',
        '& .MuiDialog-container': {
            pointerEvents: 'none',
            alignItems: 'flex-start',
            justifyContent: side === 'right' ? 'flex-end' : 'flex-start',
        },
        '& .MuiPaper-root': {
            pointerEvents: 'auto',
        },
    };

    const basePaperSx: SxProps<Theme> = {
        pointerEvents: 'auto',
        position: 'fixed',
        top: HEADER_HEIGHT + offset,
        m: 0,
        borderRadius: 2,
        width: `${COLUMN_WIDTH - horizontalMargin * 2}px`,
        maxWidth: `${COLUMN_WIDTH - horizontalMargin * 2}px`,
        ...(side === 'right' ? { right: horizontalMargin } : { left: horizontalMargin }),
    };

    const resolvedDialogSx = combineSx(baseDialogSx, containerSx, customSx);
    const resolvedPaperSx = combineSx(basePaperSx, paperSx, customPaperProps?.sx);

    const resolvedActionsSx = combineSx({ px: 3, pb: 3, gap: 1, justifyContent: 'flex-end' }, actionsProps?.sx as SxProps<Theme>);

    const mergedPaperProps = {
        ...customPaperProps,
        sx: resolvedPaperSx,
    };

    const resolvedOpen = typeof open === 'boolean' ? open : open !== null && open !== undefined;

    const handleClickAway = useCallback(() => {
        if (disableBackdropClose || !resolvedOpen) return;
        onClose();
    }, [disableBackdropClose, onClose, resolvedOpen]);

    return (
        <Dialog
            open={resolvedOpen}
            onClose={handleClose}
            hideBackdrop={hideBackdrop ?? true}
            disableAutoFocus={disableAutoFocus ?? true}
            disableEnforceFocus={disableEnforceFocus ?? true}
            fullWidth={fullWidth ?? true}
            maxWidth={maxWidth ?? false}
            sx={resolvedDialogSx}
            PaperProps={mergedPaperProps}
            keepMounted={keepMounted ?? true}
            {...restDialogProps}
        >
            <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown" touchEvent="onTouchStart">
                <Box>
                    {(title || showCloseButton || headerActions) && (
                        <DialogTitle
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1,
                                minHeight: 38,
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    mr: 1,
                                    fontFamily: 'Lexend, sans-serif',
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    lineHeight: 1.1,
                                }}
                            >
                                {title}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {headerActions}
                                {showCloseButton ? (
                                    <IconButton id={'closeDialog' + title + 'Btn'} aria-label="Close panel" size="small" onClick={onClose}>
                                        <XClose className="icon-size-medium" />
                                    </IconButton>
                                ) : null}
                            </Box>
                        </DialogTitle>
                    )}
                    <DialogContent dividers {...contentProps}>
                        {children}
                    </DialogContent>
                    {footer ? (
                        <DialogActions {...actionsProps} sx={resolvedActionsSx}>
                            {footer}
                        </DialogActions>
                    ) : null}
                </Box>
            </ClickAwayListener>
        </Dialog>
    );
};

export default SidePanelDialog;
